import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import CheckIcon from "@material-ui/icons/Check";
import clsx from "clsx";

import { ControlReview } from "@egis-smartcity/parking";
// import useCityConfig from "../../../hooks/useCityConfig";
import useControlReview from "../../../hooks/useControlReview";

const useStyles = makeStyles((theme: Theme) => ({
  /* Styles applied to the root element. */
  root: {
    position: "relative",
    width: "100%",
    height: "40px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    color: theme.palette.common.white,
    "& >*": {
      backgroundColor: theme.palette.primary.main,
    },
    zIndex: theme.zIndex.appBar - 1,
  },
  separator: {
    height: "60%",
    borderLeft: "1px solid white",
    margin: `0 ${theme.spacing(1.5)}px`,
  },
  statsLabel: {
    display: "flex",
    alignItems: "center",
    "& >:first-child": {
      marginRight: 6,
      padding: 4,
      backgroundColor: "white",
      borderRadius: "50%",
    },
  },
}));

const msg = defineMessages({
  averageControlDuration: {
    id: "productivityAgentRight.averageControlDuration.label",
    description: "Label of the average processing time.",
    defaultMessage: "Average processing time",
  },
  controlCount: {
    id: "productivityAgentRight.controlCount.label",
    description: "Label of the number of cases resolved.",
    defaultMessage: "Number of cases resolved",
  },
});

export type ProductivityAgentRightProps =
  React.HTMLAttributes<HTMLDivElement> & {
    /**
     * Control review to refresh data when an action is done.
     *
     * @todo Sort of a hack, to rework later.
     */
    controlReview?: ControlReview;
  };

/**
 * Display data about the agent activity tracking. If one of the props
 * averageControlDuration and controlCount are missing,
 * this component will stay in loading state,
 * displaying some ghost loaders.
 *
 * @param props - The ProductivityAgentRight component props.
 */
function ProductivityAgentRight(props: ProductivityAgentRightProps) {
  const { className, controlReview, ...other } = props;
  const { formatMessage: t } = useIntl();
  // const { cityConfig } = useCityConfig();
  const { controlReviewService } = useControlReview();

  // Average processing time of a control since the start of the day (in seconds)
  const [averageControlDuration, setAverageControlDuration] =
    React.useState<number>();
  // Number of decisions issued since the start of the day (FPS, NFPS ...)
  const [controlCount, setControlCount] = React.useState<number>();

  const classes = useStyles();

  const rootClasses = React.useMemo(() => {
    return clsx(classes.root, className);
  }, [className, classes.root]);

  React.useEffect(() => {
    let mounted = true;

    if (!controlReviewService || !controlReview) return;

    controlReviewService
      .getAgentSelfStatistics()
      .then((agentSelfStatistics) => {
        if (
          typeof agentSelfStatistics.averageControlDuration === "number" ||
          (typeof agentSelfStatistics.averageControlDuration === "string" &&
            !isNaN(Number(agentSelfStatistics.averageControlDuration)))
        ) {
          if (mounted) {
            setAverageControlDuration(
              Math.round(agentSelfStatistics.averageControlDuration)
            );
          }
        } else {
          if (mounted) setAverageControlDuration(undefined);
        }

        if (typeof agentSelfStatistics.controlCount === "number") {
          if (mounted) {
            setControlCount(agentSelfStatistics.controlCount);
          }
        } else {
          if (mounted) setControlCount(undefined);
        }
      })
      .catch((error) => {
        if (process.env.NODE_ENV !== "production") console.error(error);
        if (mounted) {
          setAverageControlDuration(undefined);
          setControlCount(undefined);
        }
      });

    return () => {
      mounted = false;
    };
  }, [controlReviewService, controlReview]);

  return (
    <div className={rootClasses} {...other}>
      <Typography className={classes.statsLabel}>
        <Tooltip title={<Typography>{t(msg.controlCount)}</Typography>}>
          <CheckIcon color="primary" />
        </Tooltip>
        <strong>{controlCount ?? "0"}</strong>
      </Typography>
      <div className={classes.separator} />
      <Typography className={classes.statsLabel}>
        <Tooltip
          title={<Typography>{t(msg.averageControlDuration)}</Typography>}
        >
          <AccessTimeIcon color="primary" />
        </Tooltip>
        <strong>{averageControlDuration ?? "0"}</strong> s
      </Typography>
      <div className={classes.separator} />
    </div>
  );
}

export default ProductivityAgentRight;
